import type { OperatorId } from "../core/operatorIds.ts";
import type { ThreatLevelId } from "../core/MetaState.ts";
import type { StorageAdapter } from "./StorageAdapter.ts";
import type {
  LeaderboardClient,
  LeaderboardEntryDTO,
  RankedEntry,
  SubmitResult,
} from "./LeaderboardClient.ts";

interface LeaderboardQueueItem {
  entry: LeaderboardEntryDTO;
  attempts: number;
}

export interface LeaderboardSubmitInput {
  initials: string;
  score: number;
  wave: number;
  operator: OperatorId;
  /** The run's threat level — the population this score is ranked in. */
  threatLevel: ThreatLevelId;
}

export interface LeaderboardServiceDeps {
  client: LeaderboardClient;
  storage: StorageAdapter;
  enabled?: boolean;
  maxAttempts?: number;
}

const SCORE_QUEUE_KEY = "score_queue";
const PLAYER_ID_KEY = "player_id";

export class LeaderboardService {
  private readonly client: LeaderboardClient;
  private readonly storage: StorageAdapter;
  private readonly enabled: boolean;
  private readonly maxAttempts: number;
  private playerId_: string | null = null;

  constructor(deps: LeaderboardServiceDeps) {
    this.client = deps.client;
    this.storage = deps.storage;
    this.enabled = deps.enabled ?? false;
    this.maxAttempts = deps.maxAttempts ?? 3;
  }

  isEnabled(): boolean {
    return this.enabled;
  }

  get playerId(): string {
    if (!this.playerId_) {
      throw new Error("playerId requested before init");
    }
    return this.playerId_;
  }

  async init(): Promise<void> {
    this.playerId_ = await this.ensurePlayerId();
  }

  async submitScore(entry: LeaderboardSubmitInput): Promise<SubmitResult> {
    const payload = await this.toDto(entry);
    const result = await this.client.submitScore(payload);
    if (result.ok || result.reason === "disabled") return result;

    if (
      result.reason === "invalid" ||
      result.reason === "blocked" ||
      result.reason === "rate-limited"
    ) {
      return result;
    }

    await this.enqueue(payload);
    return { ok: false, reason: "network" };
  }

  async flushQueue(): Promise<void> {
    const queue = await this.loadQueue();
    const kept: LeaderboardQueueItem[] = [];

    for (const item of queue) {
      const result = await this.client.submitScore(item.entry);
      if (result.ok || result.reason === "disabled") continue;
      if (
        result.reason === "invalid" ||
        result.reason === "blocked" ||
        result.reason === "rate-limited"
      ) {
        continue;
      }

      const nextAttempts = item.attempts + 1;
      if (nextAttempts < this.maxAttempts) {
        kept.push({ ...item, attempts: nextAttempts });
      }
    }

    await this.saveQueue(kept);
  }

  /**
   * Fetch the top entries. `includeSelf` opts into the per-row `self` highlight
   * (BL-95) by passing our own playerId to the server — used by the boot board;
   * the hot score-race path leaves it false so its response stays cacheable.
   */
  async fetchTop(
    limit: number,
    includeSelf = false,
    threatLevel?: ThreatLevelId,
  ): Promise<RankedEntry[]> {
    try {
      const selfId = includeSelf ? (this.playerId_ ?? undefined) : undefined;
      return await this.client.fetchTop(limit, selfId, undefined, threatLevel);
    } catch {
      return [];
    }
  }

  private async toDto(
    entry: LeaderboardSubmitInput,
  ): Promise<LeaderboardEntryDTO> {
    if (!this.playerId_) {
      this.playerId_ = await this.ensurePlayerId();
    }
    return {
      ...entry,
      playerId: this.playerId_,
      initials: entry.initials.toUpperCase(),
    };
  }

  private async enqueue(entry: LeaderboardEntryDTO): Promise<void> {
    const queue = await this.loadQueue();
    queue.push({ entry, attempts: 0 });
    await this.saveQueue(queue);
  }

  private async loadQueue(): Promise<LeaderboardQueueItem[]> {
    const raw = await this.storage.load(SCORE_QUEUE_KEY);
    if (!raw) return [];

    try {
      const parsed = JSON.parse(raw) as LeaderboardQueueItem[];
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }

  private async saveQueue(queue: LeaderboardQueueItem[]): Promise<void> {
    await this.storage.save(SCORE_QUEUE_KEY, JSON.stringify(queue));
  }

  private async ensurePlayerId(): Promise<string> {
    const existing = await this.storage.load(PLAYER_ID_KEY);
    if (existing && existing.length > 0) return existing;

    const generated = this.generateUuidV4();
    await this.storage.save(PLAYER_ID_KEY, generated);
    return generated;
  }

  private generateUuidV4(): string {
    const bytes = new Uint8Array(16);
    crypto.getRandomValues(bytes);
    bytes[6] = ((bytes[6] ?? 0) & 0x0f) | 0x40;
    bytes[8] = ((bytes[8] ?? 0) & 0x3f) | 0x80;

    const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, "0"));
    return [
      hex.slice(0, 4).join(""),
      hex.slice(4, 6).join(""),
      hex.slice(6, 8).join(""),
      hex.slice(8, 10).join(""),
      hex.slice(10, 16).join(""),
    ].join("-");
  }
}
